import { DEFAULT_LOCALE } from './locale';
export const SUPPORTED_COUNTRIES = ['US', 'CA', 'GB', 'AU'];
/** The default country used when no geo or account context is available. */
export const DEFAULT_COUNTRY = 'US';
const CTRY_ENDPOINT = 'https://app.honeybook.com/__ctry';
/** Fetches the visitor's ISO 3166-1 alpha-2 country code via Cloudflare. */
export async function fetchGuestCountry() {
    const res = await fetch(CTRY_ENDPOINT);
    const { 'cf-ipcountry': country } = await res.json();
    return typeof country === 'string' ? country : null;
}
/** Returns true if the visitor's IP resolves to a country HoneyBook operates in. */
export async function isSupportedCountry() {
    try {
        const country = await fetchGuestCountry();
        return (country !== null &&
            SUPPORTED_COUNTRIES.includes(country.toUpperCase()));
    }
    catch {
        return false;
    }
}
/**
 * ISO 3166-1 alpha-2 codes recognized by HoneyBook for fields that accept any region
 * (e.g. phone numbers, addresses with international destinations).
 *
 * Distinct from `SupportedCountry` above, which is the narrow set of countries
 * HoneyBook *operates in* (currently US/CA/GB/AU).
 */
export const COUNTRY_CODES = [
    'AD',
    'AE',
    'AF',
    'AG',
    'AI',
    'AL',
    'AM',
    'AO',
    'AR',
    'AS',
    'AT',
    'AU',
    'AW',
    'AX',
    'AZ',
    'BA',
    'BB',
    'BD',
    'BE',
    'BF',
    'BG',
    'BH',
    'BI',
    'BJ',
    'BL',
    'BM',
    'BN',
    'BO',
    'BQ',
    'BR',
    'BS',
    'BT',
    'BW',
    'BY',
    'BZ',
    'CA',
    'CC',
    'CD',
    'CF',
    'CG',
    'CH',
    'CI',
    'CK',
    'CL',
    'CM',
    'CN',
    'CO',
    'CR',
    'CU',
    'CV',
    'CW',
    'CX',
    'CY',
    'CZ',
    'DE',
    'DJ',
    'DK',
    'DM',
    'DO',
    'DZ',
    'EC',
    'EE',
    'EG',
    'EH',
    'ER',
    'ES',
    'ET',
    'FI',
    'FJ',
    'FK',
    'FM',
    'FO',
    'FR',
    'GA',
    'GB',
    'GD',
    'GE',
    'GF',
    'GG',
    'GH',
    'GI',
    'GL',
    'GM',
    'GN',
    'GP',
    'GQ',
    'GR',
    'GT',
    'GU',
    'GW',
    'GY',
    'HK',
    'HN',
    'HR',
    'HT',
    'HU',
    'ID',
    'IE',
    'IL',
    'IM',
    'IN',
    'IO',
    'IQ',
    'IR',
    'IS',
    'IT',
    'JE',
    'JM',
    'JO',
    'JP',
    'KE',
    'KG',
    'KH',
    'KI',
    'KM',
    'KN',
    'KP',
    'KR',
    'KW',
    'KY',
    'KZ',
    'LA',
    'LB',
    'LC',
    'LI',
    'LK',
    'LR',
    'LS',
    'LT',
    'LU',
    'LV',
    'LY',
    'MA',
    'MC',
    'MD',
    'ME',
    'MF',
    'MG',
    'MH',
    'MK',
    'ML',
    'MM',
    'MN',
    'MO',
    'MP',
    'MQ',
    'MR',
    'MS',
    'MT',
    'MU',
    'MV',
    'MW',
    'MX',
    'MY',
    'MZ',
    'NA',
    'NC',
    'NE',
    'NF',
    'NG',
    'NI',
    'NL',
    'NO',
    'NP',
    'NR',
    'NU',
    'NZ',
    'OM',
    'PA',
    'PE',
    'PF',
    'PG',
    'PH',
    'PK',
    'PL',
    'PM',
    'PR',
    'PS',
    'PT',
    'PW',
    'PY',
    'QA',
    'RE',
    'RO',
    'RS',
    'RU',
    'RW',
    'SA',
    'SB',
    'SC',
    'SD',
    'SE',
    'SG',
    'SH',
    'SI',
    'SJ',
    'SK',
    'SL',
    'SM',
    'SN',
    'SO',
    'SR',
    'SS',
    'ST',
    'SV',
    'SX',
    'SY',
    'SZ',
    'TC',
    'TD',
    'TG',
    'TH',
    'TJ',
    'TK',
    'TL',
    'TM',
    'TN',
    'TO',
    'TR',
    'TT',
    'TV',
    'TW',
    'TZ',
    'UA',
    'UG',
    'US',
    'UY',
    'UZ',
    'VA',
    'VC',
    'VE',
    'VG',
    'VI',
    'VN',
    'VU',
    'WF',
    'WS',
    'XK',
    'YE',
    'YT',
    'ZA',
    'ZM',
    'ZW'
];
/**
 * Curated additional search aliases. Country code and the localized name are already
 * searchable via the consumer. Only add aliases that are commonly typed and not
 * derivable from the localized name.
 */
const SEARCH_ALIASES = {
    AE: ['uae', 'emirates'],
    CD: ['drc', 'congo'],
    CZ: ['czechia'],
    DE: ['deutschland'],
    GB: ['uk', 'britain', 'england', 'scotland', 'wales'],
    KP: ['north korea', 'dprk'],
    KR: ['south korea', 'korea'],
    LA: ['laos'],
    MK: ['macedonia'],
    MM: ['burma'],
    NL: ['holland'],
    RU: ['russia'],
    SZ: ['swaziland'],
    TL: ['east timor'],
    TR: ['turkiye', 'türkiye'],
    TW: ['taiwan'],
    US: ['usa', 'america', 'united states of america'],
    VA: ['holy see', 'vatican'],
    VN: ['viet nam']
};
/**
 * Compute the Unicode flag emoji for an ISO 3166-1 alpha-2 code (e.g. 'US' → '🇺🇸').
 * A flag is a pair of regional-indicator symbols; each ASCII letter A–Z maps to its
 * regional-indicator counterpart at (U+1F1E6 + (letter − 'A')).
 */
const REGIONAL_INDICATOR_A = 0x1f1e6;
const ASCII_A = 'A'.charCodeAt(0);
const codeToFlagEmoji = (code) => String.fromCodePoint(...Array.from(code).map(c => c.charCodeAt(0) - ASCII_A + REGIONAL_INDICATOR_A));
/**
 * Returns the full list of countries with locale-aware names and flag emoji.
 * Names are resolved via `Intl.DisplayNames` (CLDR-quality, no bundle cost).
 * Codes whose name cannot be resolved by the runtime's CLDR data are filtered out
 * — better to drop than to surface a raw 2-letter code where a name is expected.
 */
export const getCountries = (locale = DEFAULT_LOCALE) => {
    const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    return COUNTRY_CODES.flatMap(code => {
        const name = displayNames.of(code);
        if (!name) {
            return [];
        }
        return [
            {
                code,
                name,
                flagEmoji: codeToFlagEmoji(code),
                searchTerms: SEARCH_ALIASES[code]
            }
        ];
    });
};
//# sourceMappingURL=country.js.map